import { Eye, Hand, Loader2, LogOut, Send, Wand2, X } from "lucide-react"
import { observer } from "mobx-react"
import { useState } from "react"
import { useOptionalComputers } from "../../computers/context"
import { ACCESS_LEVEL_LABELS } from "../../computers/people"
import { useOptionalCodeStore } from "../../store/context"
import { PersonGlyph } from "./PersonGlyph"

function remaining(expiresAt: string | undefined): string | null {
    if (!expiresAt) return null
    const ms = new Date(expiresAt).getTime() - Date.now()
    if (!Number.isFinite(ms)) return null
    if (ms <= 0) return "expired"
    const minutes = Math.ceil(ms / 60_000)
    if (minutes < 60) return `${minutes}m left`
    const hours = Math.floor(minutes / 60)
    if (hours < 48) return `${hours}h left`
    return `${Math.floor(hours / 24)}d left`
}

/**
 * Shown while this client is a guest on a colleague's computer. It names the
 * person, the computer, and the level so nobody mistakes someone else's
 * project for their own, and it is the one place to leave.
 */
export const GuestBanner = observer(({ onManage }: { onManage(): void }) => {
    const codeStore = useOptionalCodeStore()
    const computers = useOptionalComputers()
    const access = codeStore?.access
    if (!codeStore || !computers || !access || access.state !== "ready" || access.node) return null
    const computer = computers.state.computers.find((candidate) => candidate.nodeId === computers.scope)
    const person = computers.people.find((candidate) => candidate.nodeId === computers.scope)
    const level = person?.level && access.level !== "operate" ? person.level : access.level
    const label = level === "none" ? "No access" : ACCESS_LEVEL_LABELS[level].label
    const Icon = level === "operate" ? Wand2 : level === "propose" ? Hand : Eye
    const expiry = remaining(access.expiresAt)
    return (
        <div className="mx-3 mb-3 flex items-center gap-2 border border-border bg-base-100 px-2.5 py-2" data-guest-banner="true">
            <PersonGlyph name={person?.personName ?? computer?.name ?? "Shared"} glyph={person?.glyph} color={person?.color} />
            <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-medium text-base-content">{computer?.name ?? "Shared computer"}</span>
                <span className="flex items-center gap-1 text-[11px] text-muted">
                    <Icon className="h-3 w-3" />
                    {label}
                    {access.paused ? " · paused" : ""}
                    {expiry ? ` · ${expiry}` : ""}
                </span>
            </span>
            <button
                type="button"
                className="btn flex h-7 w-7 items-center justify-center text-muted hover:bg-base-300/60 hover:text-base-content"
                title="Manage shared computers"
                aria-label="Manage shared computers"
                onClick={onManage}
            >
                <LogOut className="h-3.5 w-3.5" />
            </button>
        </div>
    )
})

/**
 * Replaces the composer while viewing someone else's project. At propose
 * level it is the suggestion box: nothing runs until the owner approves.
 */
export const GuestComposerNotice = observer(({ taskId, workspaceId }: { taskId: string; workspaceId: string }) => {
    const codeStore = useOptionalCodeStore()
    const computers = useOptionalComputers()
    const [draft, setDraft] = useState("")
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const access = codeStore?.access
    if (!codeStore || !access || access.state !== "ready" || access.node) return null
    const person = computers?.people.find((candidate) => candidate.nodeId === computers.scope)
    const who = person?.personName ?? "the owner"
    const propose = person?.level === "propose"
    const mine = codeStore.proposals.proposalsFor(taskId).filter((proposal) => proposal.by.principalId === access.principalId)
    const pending = mine.filter((proposal) => proposal.status === "pending")
    const recent = mine.filter((proposal) => proposal.status !== "pending").slice(-3)

    const send = async () => {
        setBusy(true)
        setError(null)
        try {
            await codeStore.proposals.propose(taskId, workspaceId, draft)
            setDraft("")
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setBusy(false)
        }
    }

    const withdraw = async (proposalId: string) => {
        setBusy(true)
        setError(null)
        try {
            await codeStore.proposals.resolve(taskId, workspaceId, proposalId, "withdrawn")
        } catch (cause) {
            setError(cause instanceof Error ? cause.message : String(cause))
        } finally {
            setBusy(false)
        }
    }

    if (!propose) {
        return (
            <div
                className="mx-auto mb-4 flex w-full max-w-3xl items-center gap-3 border border-border bg-base-200/60 px-4 py-3 text-xs text-muted"
                data-guest-composer="true"
            >
                <Eye className="h-4 w-4 flex-none" />
                <span className="min-w-0 flex-1 leading-5">You're viewing {who}'s project. Suggesting the next step needs propose access.</span>
            </div>
        )
    }

    return (
        <div className="mx-auto mb-4 flex w-full max-w-3xl flex-col gap-2 border border-primary/30 bg-base-100 px-3 py-3" data-guest-composer="propose">
            {pending.length > 0 && (
                <div className="flex flex-col gap-1">
                    {pending.map((proposal) => (
                        <div key={proposal.id} className="flex min-w-0 items-center gap-2 border border-border bg-base-200 px-2 py-1.5 text-xs">
                            <Hand className="h-3.5 w-3.5 flex-none text-primary" />
                            <span className="shrink-0 font-medium text-primary">Waiting for {who}</span>
                            <span className="min-w-0 flex-1 truncate text-base-content" title={proposal.input}>
                                {proposal.input}
                            </span>
                            <button
                                type="button"
                                className="btn shrink-0 p-1 text-muted hover:bg-base-300 hover:text-base-content disabled:opacity-50"
                                onClick={() => void withdraw(proposal.id)}
                                disabled={busy}
                                aria-label="Withdraw suggestion"
                                title="Withdraw this suggestion"
                            >
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            {recent.length > 0 && (
                <div className="flex flex-col gap-0.5 text-[11px] text-muted">
                    {recent.map((proposal) => (
                        <div key={proposal.id} className="truncate" title={proposal.input}>
                            {proposal.status === "approved" ? "Approved" : proposal.status === "declined" ? "Declined" : "Withdrawn"} · {proposal.input}
                            {proposal.note ? ` · “${proposal.note}”` : ""}
                        </div>
                    ))}
                </div>
            )}
            <div className="flex items-end gap-2">
                <textarea
                    className="input min-h-10 w-full resize-y border border-border bg-input px-3 py-2 text-sm text-base-content outline-none focus:border-primary"
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    placeholder={`Suggest the next step. ${who} approves before anything runs.`}
                    rows={2}
                    maxLength={8_000}
                    onKeyDown={(event) => {
                        if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && draft.trim() && !busy) {
                            event.preventDefault()
                            void send()
                        }
                    }}
                />
                <button
                    type="button"
                    className="btn flex h-10 flex-none items-center gap-1.5 bg-primary px-3 text-xs font-medium text-primary-content hover:bg-primary/90 disabled:opacity-50"
                    onClick={() => void send()}
                    disabled={busy || !draft.trim()}
                >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Suggest
                </button>
            </div>
            {error && (
                <div role="alert" className="text-xs text-error">
                    {error}
                </div>
            )}
        </div>
    )
})
